export const OnboardingStatus=Object.freeze({
  NOT_STARTED:'not_started',
  IN_PROGRESS:'in_progress',
  COMPLETED:'completed',
  SKIPPED:'skipped'
});

const STATUSES=new Set(Object.values(OnboardingStatus));

function requiredUserId(value){
  const id=String(value||'').trim();
  if(!id)throw new TypeError('userId is required');
  return id;
}

function stepName(value){
  if(value==null||value==='')return null;
  const step=String(value).trim();
  if(!step||step.length>64)throw new TypeError('Invalid onboarding step');
  return step;
}

function completedStepList(values=[]){
  if(!Array.isArray(values))throw new TypeError('completedSteps must be an array');
  const seen=new Set(),result=[];
  for(const value of values){
    const step=stepName(value);
    if(step&&!seen.has(step)){seen.add(step);result.push(step);}
  }
  return result;
}

export function createOnboardingProgress({
  userId,
  onboardingVersion=1,
  status=OnboardingStatus.NOT_STARTED,
  currentStep=null,
  completedSteps=[],
  startedAt=null,
  completedAt=null
}={}){
  const version=Number(onboardingVersion);
  if(!Number.isInteger(version)||version<1)throw new TypeError('onboardingVersion must be a positive integer');
  const nextStatus=String(status||'');
  if(!STATUSES.has(nextStatus))throw new TypeError('Unsupported onboarding status');

  return Object.freeze({
    userId:requiredUserId(userId),
    onboardingVersion:version,
    status:nextStatus,
    currentStep:stepName(currentStep),
    completedSteps:Object.freeze(completedStepList(completedSteps)),
    startedAt:startedAt?String(startedAt):null,
    completedAt:completedAt?String(completedAt):null
  });
}

export function startOnboarding(progress,{step,now=new Date().toISOString()}={}){
  const current=createOnboardingProgress(progress);
  return createOnboardingProgress({
    ...current,
    status:OnboardingStatus.IN_PROGRESS,
    currentStep:step??current.currentStep,
    startedAt:current.startedAt||now,
    completedAt:null
  });
}

export function completeOnboardingStep(progress,step,{nextStep=null}={}){
  const current=createOnboardingProgress(progress);
  const completed=completedStepList([...current.completedSteps,step]);
  return createOnboardingProgress({
    ...current,
    status:OnboardingStatus.IN_PROGRESS,
    currentStep:nextStep,
    completedSteps:completed
  });
}

export function skipOnboarding(progress,{now=new Date().toISOString()}={}){
  const current=createOnboardingProgress(progress);
  return createOnboardingProgress({...current,status:OnboardingStatus.SKIPPED,currentStep:null,completedAt:null,startedAt:current.startedAt||now});
}

export function completeOnboarding(progress,{now=new Date().toISOString()}={}){
  const current=createOnboardingProgress(progress);
  return createOnboardingProgress({...current,status:OnboardingStatus.COMPLETED,currentStep:null,completedAt:now,startedAt:current.startedAt||now});
}


function orderedStepList(values=[]){
  if(!Array.isArray(values)||values.length===0)throw new TypeError('steps must be a non-empty array');
  const steps=completedStepList(values);
  if(steps.length===0)throw new TypeError('steps must include at least one valid step');
  return steps;
}

export function resolveOnboardingResumeStep(progress,{steps}={}){
  const current=createOnboardingProgress(progress);
  if(current.status===OnboardingStatus.COMPLETED)return null;

  const ordered=orderedStepList(steps);
  if(current.status===OnboardingStatus.IN_PROGRESS&&current.currentStep&&ordered.includes(current.currentStep)){
    return current.currentStep;
  }

  const completed=new Set(current.completedSteps);
  return ordered.find(step=>!completed.has(step))||null;
}

export function resumeOnboarding(progress,{steps,now=new Date().toISOString()}={}){
  const current=createOnboardingProgress(progress);
  if(current.status===OnboardingStatus.COMPLETED)return current;

  const step=resolveOnboardingResumeStep(current,{steps});
  if(!step)return completeOnboarding(current,{now});

  return createOnboardingProgress({
    ...current,
    status:OnboardingStatus.IN_PROGRESS,
    currentStep:step,
    startedAt:current.startedAt||now,
    completedAt:null
  });
}

export function onboardingProgressToRow(progress){
  const value=createOnboardingProgress(progress);
  return Object.freeze({
    user_id:value.userId,
    onboarding_version:value.onboardingVersion,
    status:value.status,
    current_step:value.currentStep,
    completed_steps:[...value.completedSteps],
    started_at:value.startedAt,
    completed_at:value.completedAt
  });
}

export function onboardingProgressFromRow(row={}){
  return createOnboardingProgress({
    userId:row.user_id,
    onboardingVersion:row.onboarding_version,
    status:row.status,
    currentStep:row.current_step,
    completedSteps:row.completed_steps,
    startedAt:row.started_at,
    completedAt:row.completed_at
  });
}
